import {
  Error,
  SimpleForm,
  required,
  useQuery,
  useTranslate,
} from "react-admin";
import { maxDate, minDate } from "./useValidation";

import ActivitiesInput from "./inputs/ActivitiesInput";
import DeliverablesInput from "./inputs/DeliverablesInput";
import DurationInput from "./inputs/DurationInput";
import { MYDateInput } from "../component/input";
import MilestonesInput from "./inputs/MilestonesInput";
import React from "react";
import ValidationBox from "./ValidationBox";
import { get } from "lodash";
import moment from "moment";
import {
  DebouncedTextInput,
  Group,
  GroupItem,
  GroupTitle,
  Toolbar,
} from "ra-friendsofbabba";

const Form = ({
  dispatch,
  errors,
  permissions,
  validate,
  nextIndex = undefined,
  ...props
}) => {
  const translate = useTranslate();
  const { record } = props;
  const referenceId = get(record, "executive_project_id");
  // Devo recuperare l'ultimo indice valido per poter aggiungere un work package
  // e mostrare il suo progressivo all'interno dell'elenco.
  const { data, loading, error } = useQuery({
    type: "get",
    resource: "executive-project-work-packages/config",
    payload: { executive_project_id: referenceId },
  });
  if (error) return <Error />;
  if (loading) return null;
  // Il valore di ordinamento va recuperato prima dal record (che ha priorità),
  // se non esiste li allora provo a recuperarlo direttamente dalla chiamata
  // che restituisce l'indice successivo applicabile.
  const index = get(record, "order_index", get(data, "data.next_index", 1));
  // Lo start dei WP può avvenire in una data maggiore o uguale a quella di assunzione
  // del ricercatore associato al progetto.
  const hiringDate = moment(get(data, "data.hiring_date"));
  const minAllowedDate = hiringDate.clone().startOf("month");
  const maxAllowedDate = moment(minAllowedDate)
    // Se il ricercatore è stato assunto dopo il 15 del mese,
    // devo considerare la possibilità di poter caricare un mese in più.
    // Indubbiamente il totale dovrà sempre fare 36.
    .add(36 + (hiringDate.date() > 15 ? 1 : 0), "month")
    .startOf("month");
  const handleListIndexRenderer = (subRecordIndex) =>
    `${index}.${subRecordIndex + 1}`;

  return (
    <SimpleForm
      {...props}
      toolbar={
        <Toolbar
          backTab={1}
          backReference="executive-projects"
          backReferenceTarget="executive_project_id"
        />
      }
      initialValues={{
        ...record,
        order_index: index,
        start_date: minAllowedDate.format("YYYY-MM-DD"),
        duration: 12,

        // Trasmetto queste proprietà ma in realtà non saranno mai salvate.
        // Saranno utilizzate solo per la validazione.
        min_allowed_date: minAllowedDate,
        max_allowed_date: maxAllowedDate,
      }}
    >
      <Group wrapper>
        <GroupTitle
          title={translate("resources.executive-project-work-packages.title", {
            index,
          })}
          subTitle={translate("resources.executive-project-work-packages.help")}
        />
      </Group>
      <ValidationBox />
      <DebouncedTextInput
        source="title"
        fullWidth
        validate={required()}
        maxLength={200}
      />
      <MYDateInput
        source="start_date"
        validate={[required(), minDate(), maxDate()]}
        lg={2}
        md={3}
        sm={6}
      />
      <Group fullWidth>
        <GroupItem lg={4} md={6}>
          <DurationInput
            source="duration"
            minAllowedDate={minAllowedDate}
            maxAllowedDate={maxAllowedDate}
          />
        </GroupItem>
      </Group>

      <DebouncedTextInput
        source="results"
        fullWidth
        multiline
        maxLength={1500}
        validate={required()}
      />

      <ActivitiesInput
        source="activities"
        fullWidth
        validate={required()}
        listIndexRenderer={handleListIndexRenderer}
      />

      <DeliverablesInput
        source="deliverables"
        fullWidth
        validate={required()}
        listIndexRenderer={handleListIndexRenderer}
      />
      <MilestonesInput
        source="milestones"
        fullWidth
        listIndexRenderer={handleListIndexRenderer}
      />
    </SimpleForm>
  );
};

export default Form;
